import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, CheckCircle2, SkipForward, Circle, Map, Sparkles } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { EmptyState, Spinner, useToast } from "@/components/earnos/kit";
import { colors } from "@/lib/theme";

const STATUS_META = {
  done: { color: colors.success, Icon: CheckCircle2 },
  skipped: { color: colors.onSurfaceSecondary, Icon: SkipForward },
  pending: { color: colors.brandSecondary, Icon: Circle },
};

export default function Plan() {
  const navigate = useNavigate();
  const toast = useToast();
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const d = await apiFetch("/moves");
      setMoves(d.moves);
    } catch (e) {
      setLoadError(e.message || "Service unavailable. Please retry.");
      toast.show(e?.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (loadError && !loading) return <LoadError title="30-day plan" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} data-testid="plan-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">30-Day Plan</h1>
      </div>

      {loading ? (
        <Spinner className="py-24" />
      ) : moves.length === 0 ? (
        <EmptyState icon={Map} title="No plan yet" message="Create your Income Engine to generate a 30-day plan." actionTitle="Go to Engine" onAction={() => navigate("/engine")} />
      ) : (
        <>
          <div className="flex items-center gap-2 rounded-xl bg-brand/10 border border-brand/30 p-4">
            <Sparkles size={18} className="text-brand-light shrink-0" />
            <p className="text-base text-ink-3 leading-relaxed">Your plan can adapt anytime. Choose a new pace — no shame if you fall behind.</p>
          </div>

          <div className="mt-6">
            {moves.map((m, i) => {
              const meta = STATUS_META[m.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div key={m.move_id} className="flex gap-3">
                  <div className="flex flex-col items-center w-9">
                    <div className="w-9 h-9 rounded-full border-2 bg-surface flex items-center justify-center shrink-0" style={{ borderColor: meta.color }}>
                      <Icon size={16} style={{ color: meta.color }} />
                    </div>
                    {i < moves.length - 1 ? <div className="w-0.5 flex-1 bg-line my-1" /> : null}
                  </div>
                  <button onClick={() => navigate(`/move/${m.move_id}`)} data-testid={`plan-day-${m.day}`} className="flex-1 text-left rounded-xl border border-line bg-surface-2 p-4 mb-3 hover:border-line-strong transition-colors">
                    <p className="earnos-kicker">DAY {m.day}</p>
                    <p className={`text-base font-bold mt-0.5 ${m.status === "done" ? "line-through text-ink-2" : "text-ink"}`}>{m.title}</p>
                    <div className="flex items-center gap-1 mt-1 text-sm text-ink-2"><Clock size={12} /><span>{m.estimated_time}</span></div>
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
